import {useState, useEffect} from 'react';
import { searchOnEnter } from '../../components/searchOnEnter';
import Sidebar from '../../components/AdminDashboard/Sidebar';
import TopBar from '../../components/AdminDashboard/TopBar';
import MetaData from '../../layouts/MetaData';
import Loader from '../../layouts/Loader';
import { useDispatch, useSelector} from 'react-redux';
import { DropdownButton, Dropdown } from 'react-bootstrap';
import { 
    getInactivePatients,
    patientReactivate,
    bulkSetDeactivationReason
} from '../../actions/adminActions';
import ReactivatePatientModal from '../../components/ReactivatePatientModal';
import SetDeactivationReasonModal from '../../components/SetDeactivationReasonModal';
import { CSVLink } from 'react-csv';
import {
    DEACTIVATION_REASONS,
    NOT_RECORDED,
    NOT_RECORDED_CODE,
    countByReason,
    isDeceasedPatient,
    isSuperAdmin,
    lastFirst,
} from '../../utils/deactivationReasons';
import { canReactivatePatient, describeDeactivation } from '../../utils/patientStatus';
import { Link, useHistory } from 'react-router-dom';
import { useAlert } from 'react-alert';
import {Badge, Table } from 'react-bootstrap';
import moment from 'moment-timezone';
import {UPDATE_PATIENT_RESET} from '../../constants/adminConstants';
import { useSortableTable, SortableTh, personName } from '../../components/SortableTable';
import { formatDate, formatDob } from '../../utils/dateFormat';

const INACTIVE_SORT_COLUMNS = {
    name: { type: 'name', value: personName },
    dob: { type: 'date', value: (p) => p?.DOB },
    status: { type: 'boolean', value: (p) => p?.block },
    consent: { type: 'string', value: (p) => p?.patientType },
    deactivatedOn: { type: 'date', value: (p) => p?.deactivation?.at, defaultDir: 'desc' },
    reason: { type: 'string', value: (p) => p?.deactivation?.reasonLabel || p?.deactivation?.reason || NOT_RECORDED },
};

const reasonCodeOf = (p) => p?.deactivation?.reasonCode || NOT_RECORDED_CODE;
const etDate = (d) => (d ? formatDate(d) : '');

// CSV (opens in Excel) of the rows currently shown, including the deactivation reason.
export const inactiveCsvRows = (rows = []) => rows.map((p) => ({
    'Name': lastFirst(p),
    'EHR-ID': p?.emrId || '',
    'DOB': p?.DOB ? formatDob(p.DOB) : '',
    'Consent Role (s)': p?.patientType || '',
    'Deactivated On': etDate(p?.deactivation?.at),
    'Effective Date': etDate(p?.deactivation?.effectiveDate),
    'Reason Code': p?.deactivation?.reasonCode || '',
    'Reason': p?.deactivation?.reasonLabel || NOT_RECORDED,
    'Date of Death': etDate(p?.deactivation?.dateOfDeath),
    'Deceased': isDeceasedPatient(p) ? 'Yes' : '',
    'Note': p?.deactivation?.note || '',
    'Deactivated By': p?.deactivation?.by || '',
}));



const InactivePatients = () => {

    const dispatch = useDispatch();
    const history = useHistory();
    const [smShow, setSmShow] = useState(false); //small confirm modal
    const [patientModel, setPatientModel] = useState(null);
    const [patientToDelete, setPatientToDelete] = useState(null);
    const alert = useAlert();
    const { loading, error, inactivePts} = useSelector(state => state.inactivePatients);
    const { isUpdated, loading: updating } = useSelector(state => state.admin);
    const { user } = useSelector(state => state.auth);
    const mayReactivate = canReactivatePatient(user?.role);
    const maySetReason = canReactivatePatient(user?.role);
    const [reasonFilter, setReasonFilter] = useState('');
    const [selected, setSelected] = useState([]);
    const [showSetReason, setShowSetReason] = useState(false);
    const [settingReason, setSettingReason] = useState(false);
    const [reactivateTarget, setReactivateTarget] = useState(null);
    const reasonCounts = countByReason(inactivePts || []);

    const tableSort = useSortableTable(inactivePts, { id: 'admin.inactivePatients', columns: INACTIVE_SORT_COLUMNS, defaultSort: { key: 'name', dir: 'asc' } });
    const [query, setQuery] = useState("");
    const keys = ["firstname", "lastname", "email", "npinumber", "phone1"];
    const visibleRows = (tableSort.rows || [])
        .filter(item => keys.some(key => item[key]?.toLowerCase().includes(query)))
        .filter(item => !reasonFilter || reasonCodeOf(item) === reasonFilter);
    const selectable = (p) => maySetReason && !p?.deactivation?.reasonCode;
    const toggleSelected = (id) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
    const selectableVisible = visibleRows.filter(selectable).map((p) => p._id);
    const allSelected = selectableVisible.length > 0 && selectableVisible.every((id) => selected.includes(id));

    const setReason = async (body) => {
        setSettingReason(true);
        try {
            const res = await bulkSetDeactivationReason(selected, body);
            alert.success(`Reason set for ${res.updated} patient${res.updated === 1 ? '' : 's'}${res.skipped?.length ? ` (${res.skipped.length} skipped)` : ''}`);
            setSelected([]);
            setShowSetReason(false);
            dispatch(getInactivePatients());
        } catch (e) {
            alert.error(e?.response?.data?.message || e.message);
        } finally {
            setSettingReason(false);
        }
    };



    useEffect(() => {
        if(error){
            alert.error(error); return;
        }  
        
        if(isUpdated){
            alert.success('Status Changed');
            dispatch({type: UPDATE_PATIENT_RESET});
            dispatch(getInactivePatients());
            setSmShow(false);
        }

        dispatch(getInactivePatients());    
    
    }, [dispatch, alert, error, isUpdated]);
    

    const refreshHandler = () => {
        dispatch(getInactivePatients());
    }

    const reactivatePatient = (reason, confirmDeceased) => {
        dispatch(patientReactivate(patientModel, reason, confirmDeceased));
    }

    
  return (
    <>
                <MetaData title="Inactive Patients"/>
                <Sidebar />    

                <section className="home-section">
                {/* TopBar */}
                <TopBar />

                {loading ? <Loader /> : (
                <>   
                {/*  patients List Filter Section */}
                <div className="shadow-lg p-3 mb-5 mr-4 ml-4 rounded-card" style={{backgroundColor: '#FAFAFA'}}>
                    <div className="home-content">
                        
                        <div className="row-display">
                            <div className="col-md-7">
                                <h5 className='title' style={{color: 'red'}}>
                                    <i className="bx bx-user"></i> <strong>&nbsp;Inactive Patients </strong>
                                </h5>
                                <small style={{color: 'gray'}}>Review only. Inactive patients are excluded from dashboard counts; reactivation is audited.</small>
                            </div>

                           <div className="col-md-5 row-display"> 
                            <button className="edit-pill" onClick={() => history.goBack()}>
                                <i className='bx bxs-user'></i> &nbsp;Active Patients
                            </button>

                            
                            <div className="row-display">    
                                <input type="text" 
                                    placeholder="Search patient..." 
                                    className="form-control" 
                                    style={{width: '200px'}}
                                    onChange={e => setQuery(e.target.value)}
                                    onKeyDown={searchOnEnter(setQuery)}
                                />
                                &nbsp;&nbsp;
                                <button className="btn btn-secondary" onClick={refreshHandler}><i className='bx bx-refresh'></i></button> &nbsp;
                            </div>
                                
                            </div>                
                        </div>
                    </div>  
                <div className="row-display mt-2" data-testid="reason-summary" style={{flexWrap: 'wrap', gap: '6px', justifyContent: 'flex-start'}}>
                    <select
                        aria-label="Filter by reason"
                        className="form-control"
                        style={{width: '280px'}}
                        value={reasonFilter}
                        onChange={e => setReasonFilter(e.target.value)}
                    >
                        <option value="">All reasons ({inactivePts?.length || 0})</option>
                        {[...DEACTIVATION_REASONS, { code: NOT_RECORDED_CODE, label: NOT_RECORDED }].map(r => (
                            <option key={r.code} value={r.code}>
                                {r.label} ({reasonCounts.find(c => c.code === r.code)?.count || 0})
                            </option>
                        ))}
                    </select>
                    {reasonCounts.map(c => (
                        <button
                            key={c.code}
                            type="button"
                            className={`btn btn-sm ${reasonFilter === c.code ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setReasonFilter(reasonFilter === c.code ? '' : c.code)}
                        >
                            {c.label}: <b>{c.count}</b>
                        </button>
                    ))}
                    <span style={{flex: 1}} />
                    {maySetReason && <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={!selected.length}
                        onClick={() => setShowSetReason(true)}
                    >
                        Set reason{selected.length ? ` (${selected.length})` : ''}
                    </button>}
                    <button className="edit-pill">
                        <i className='bx bxs-cloud-upload'></i> <CSVLink data={inactiveCsvRows(visibleRows)} filename={`inactive-patients-${moment().tz('America/New_York').format('YYYY-MM-DD')}.csv`} style={{color: '#fff', textDecoration: 'none'}}> Export CSV / Excel</CSVLink>
                    </button>
                </div>
                <br />


                    {/* Patient List Card */}
                        <div className="col-md-12">
                        {inactivePts && <>
                            <Table hover striped bordered>
                            <thead align="center">
                                <tr>
                                {maySetReason && <th>
                                    <input
                                        type="checkbox"
                                        aria-label="Select all without a reason"
                                        checked={allSelected}
                                        disabled={!selectableVisible.length}
                                        onChange={() => setSelected(allSelected ? selected.filter(id => !selectableVisible.includes(id)) : [...new Set([...selected, ...selectableVisible])])}
                                    />
                                </th>}
                                <SortableTh sortKey="name" sort={tableSort}>Name</SortableTh>
                                <SortableTh sortKey="dob" sort={tableSort}>DOB</SortableTh>
                                <SortableTh sortKey="status" sort={tableSort}>Acc Status</SortableTh>
                                <SortableTh sortKey="consent" sort={tableSort}>Consent Role (s)</SortableTh>
                                <SortableTh sortKey="deactivatedOn" sort={tableSort}>Deactivated On</SortableTh>
                                <SortableTh sortKey="reason" sort={tableSort}>Reason</SortableTh>
                                <th>ACTION</th> 
                                </tr>
                            </thead>
                            <tbody>
                            
                            {inactivePts && inactivePts.length > 0 ? <> 
                                {visibleRows.map((patient, index) => (
                                <tr key={index}>  
                                {maySetReason && <td>
                                    {selectable(patient) && <input
                                        type="checkbox"
                                        aria-label={`Select ${lastFirst(patient)}`}
                                        checked={selected.includes(patient._id)}
                                        onChange={() => toggleSelected(patient._id)}
                                    />}
                                </td>}
                                <td>
                                    <Link style={{textDecoration: 'none', color: "dodgerblue", fontWeight: 'bold'}} 
                                        to={{ pathname: "/patientProfile", state: {patientid: patient?._id, batterySignalStatus: patient?.batterySignals?.sig}}}>
                                        {patient?.firstname} {patient?.lastname}
                                    </Link>
                                    {isDeceasedPatient(patient) && <p><Badge bg="dark text-white">Deceased</Badge></p>}
                                </td>
                                
                                <td> {formatDob(patient?.DOB)} <p><Badge bg="dark text-white">{patient?.gender}</Badge></p></td> 
                                
                                {patient?.block === false ? <td>
                                    <Badge pill bg="success text-white" style={{fontSize: '12px', fontWeight: 300}}>Active</Badge>
                                    </td> : <td><Badge pill bg="danger text-white" style={{fontSize: '12px'}}>Inactive</Badge>
                                </td>}
                                

                                <td>{patient?.patientType}</td>

                                <td>{describeDeactivation(patient?.deactivation).date}
                                    {patient?.deactivation?.by && <p><small style={{color: 'gray'}}>by {patient.deactivation.by}</small></p>}
                                </td>

                                <td>{patient?.deactivation?.reasonLabel || describeDeactivation(patient?.deactivation).reason}
                                    {patient?.deactivation?.dateOfDeath && <p className="m-0"><small style={{color: 'gray'}}>Date of death {etDate(patient.deactivation.dateOfDeath)}</small></p>}
                                    {patient?.deactivation?.note && <p className="m-0"><small style={{color: 'gray'}}>{patient.deactivation.note}</small></p>}
                                </td>

                                <td>
                                <DropdownButton id="dropdown-basic-button">
                                    <Dropdown.Item>
                                        <Link className="link" to={{ pathname: "/patientProfile", state: {patientid: patient?._id, batterySignalStatus: patient?.batterySignals?.sig}}}>View Profile</Link>
                                    </Dropdown.Item>

                                    {mayReactivate && <Dropdown.Item as="button" style={{color: 'green'}} onClick={() => {setSmShow(true); setPatientModel(patient?._id); setPatientToDelete(patient?.lastname); setReactivateTarget(patient)}}>
                                        Reactivate Profile
                                    </Dropdown.Item>}
                                </DropdownButton>
                                </td>
                            </tr>    
                            ))}
                            </> : <div>
                                <small className="not-found-text">Patients Not Found</small>
                            </div> }
                            
                            </tbody>
                            
                            </Table> 

                                <ReactivatePatientModal
                                    show={smShow}
                                    patientName={patientToDelete}
                                    onCancel={() => setSmShow(false)}
                                    onConfirm={reactivatePatient}
                                    submitting={updating}
                                    deceased={isDeceasedPatient(reactivateTarget)}
                                    canOverrideDeceased={isSuperAdmin(user)}
                                />
                                <SetDeactivationReasonModal
                                    show={showSetReason}
                                    count={selected.length}
                                    onCancel={() => setShowSetReason(false)}
                                    onConfirm={setReason}
                                    submitting={settingReason}
                                />
                        </>}                      
                        </div>
                    </div>
                </>
            )}
            </section>
        </>
    )
}

export default InactivePatients